import React, { useState, useCallback, useEffect } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Typography,
  Paper,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Alert,
  Grid,
  Link,
  CircularProgress,
  Skeleton,
  Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { Asset } from '../types/asset.types';
import {
  AssetDetailsResponse,
  LookMetadataResponse,
  MoveMetadataResponse,
  SongMetadataDetailsResponse,
  StarMetadataResponse,
  WorldMetadataResponse,
} from '../contracts/openapi/assetContracts';
import { METADATA_LABEL_KEYS } from '../constants/assetDetailMetadata';
import assetService from '../api/assetService';
import { registrationService } from '../services/api/registration.service';
import {
  getVariantUploadConfig,
  isFileAllowedForLayer,
  VARIANT_MAX_FILE_SIZE_BYTES,
  isVariantLayer,
} from '../constants/variantUpload';
import AssetThumbnail from '../components/common/AssetThumbnail';
import ReadOnlyMetadataGrid from '../components/common/ReadOnlyMetadataGrid';
import { getVariantTypeOptions } from '../constants/variantTypes';

/** Map AssetDetailsResponse to Asset for BaseAssetSummary (same shape as AssetDetailPage baseAssetForThumbnail). */
function mapDetailsToAsset(details: AssetDetailsResponse): Asset {
  const mfa = details.machineFriendlyAddress ?? '';
  return {
    id: details.id,
    _id: details.id,
    name: details.humanFriendlyName ?? '',
    friendlyName: details.humanFriendlyName ?? '',
    machineFriendlyName: mfa,
    layer: details.layer,
    categoryCode: details.categoryCode ?? details.categoryName ?? '',
    subcategoryCode: details.subcategoryCode ?? details.subcategoryName ?? '',
    type: 'base',
    imageUrl: details.fileUrl ?? '',
    files: [],
    metadata: {
      fileUrl: details.fileUrl,
      videoUrl: details.fileUrl,
    },
    createdAt: '',
    updatedAt: '',
    createdBy: '',
    status: 'active',
  };
}

const LAYER_LABELS: Record<string, string> = {
  G: 'Song',
  S: 'Star',
  L: 'Look',
  M: 'Move',
  W: 'World',
};

type LayerMetadataResponse =
  | SongMetadataDetailsResponse
  | LookMetadataResponse
  | MoveMetadataResponse
  | StarMetadataResponse
  | WorldMetadataResponse;

const CreateVariantPage: React.FC = () => {
  const { baseAssetId } = useParams<{ baseAssetId: string }>();
  const navigate = useNavigate();

  const [details, setDetails] = useState<AssetDetailsResponse | null>(null);
  const [layerMetadata, setLayerMetadata] = useState<LayerMetadataResponse | null>(null);
  const [tags, setTags] = useState<{ tags: string[] } | null>(null);
  const [metadataError, setMetadataError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [variantType, setVariantType] = useState<string>('');
  const [variantName, setVariantName] = useState<string>('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string>('');
  const [submitError, setSubmitError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadDetails = useCallback(async () => {
    if (!baseAssetId?.trim()) return;
    setError(null);
    setMetadataError(null);
    setLoading(true);
    try {
      const id = baseAssetId.trim();
      const data = await assetService.getAssetDetails(id);
      setDetails(data);

      const [tagsRes, layer] = [await assetService.getAssetTags(id).catch(() => ({ tags: [] })), data.layer];
      setTags(tagsRes ?? { tags: [] });

      if (['G', 'L', 'M', 'S', 'W'].includes(layer)) {
        try {
          if (layer === 'G') setLayerMetadata(await assetService.getSongMetadata(id));
          else if (layer === 'L') setLayerMetadata(await assetService.getLookMetadata(id));
          else if (layer === 'M') setLayerMetadata(await assetService.getMoveMetadata(id));
          else if (layer === 'S') setLayerMetadata(await assetService.getStarMetadata(id));
          else if (layer === 'W') setLayerMetadata(await assetService.getWorldMetadata(id));
        } catch (e) {
          setMetadataError(e instanceof Error ? e.message : 'Failed to load metadata');
          setLayerMetadata(null);
        }
      } else {
        setLayerMetadata(null);
      }
    } catch (err: any) {
      setError(err?.message ?? 'Failed to load asset details.');
      setDetails(null);
      setLayerMetadata(null);
      setTags(null);
    } finally {
      setLoading(false);
    }
  }, [baseAssetId]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const layer = details?.layer ?? '';
  const baseAsset = details ? mapDetailsToAsset(details) : null;
  const displayNameFromMetadata =
    !details || !layerMetadata
      ? undefined
      : layer === 'G'
        ? (layerMetadata as SongMetadataDetailsResponse).songName
        : layer === 'S'
          ? (layerMetadata as StarMetadataResponse).starName
          : layer === 'L'
            ? (layerMetadata as LookMetadataResponse).outfitName
            : layer === 'M'
              ? (layerMetadata as MoveMetadataResponse).moveName
              : layer === 'W'
                ? (layerMetadata as WorldMetadataResponse).worldName
                : undefined;
  const displayName =
    details && layerMetadata
      ? displayNameFromMetadata ?? details.humanFriendlyName ?? details.id
      : details
        ? details.humanFriendlyName ?? details.id
        : '';
  const taxonomyPath = details
    ? [details.categoryName ?? details.categoryCode, details.subcategoryName ?? details.subcategoryCode]
        .filter(Boolean)
        .join(' / ')
    : '';
  const mfa = details?.machineFriendlyAddress ?? '';
  const uploadConfig = layer ? getVariantUploadConfig(layer) : null;
  const accept = uploadConfig?.accept ?? '';

  const variantTypeOptions = getVariantTypeOptions(layer);
  const isFormValid =
    !!baseAsset &&
    isVariantLayer(layer) &&
    !!variantType &&
    variantType.length <= 255 &&
    !!variantName.trim() &&
    variantName.trim().length <= 255 &&
    !!file &&
    !fileError;

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      setFileError('');
      if (!selected) {
        setFile(null);
        return;
      }
      if (selected.size > VARIANT_MAX_FILE_SIZE_BYTES) {
        setFileError(`File must be 100 MB or smaller.`);
        setFile(null);
        return;
      }
      if (!isFileAllowedForLayer(selected, layer)) {
        setFileError(
          `Invalid file type for ${LAYER_LABELS[layer] || layer}. Allowed: ${uploadConfig?.extensions.join(', ') ?? 'see layer'}`
        );
        setFile(null);
        return;
      }
      setFile(selected);
    },
    [layer, uploadConfig]
  );

  const handleSubmit = async () => {
    if (!baseAsset || !details || !file || !variantType || !variantName.trim()) return;
    setSubmitError('');
    setIsSubmitting(true);
    try {
      const id = details.id;
      await registrationService.registerVariant(layer, file, {
        baseAssetId: id,
        variantName: variantName.trim(),
        variantType,
      });
      navigate(`/assets/${details.id}`);
    } catch (err: any) {
      setSubmitError(err?.message ?? 'Variant registration failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!baseAssetId?.trim()) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Alert severity="info">
              Select an asset from Browse to create a variant.{' '}
              <Link component={RouterLink} to="/search-assets">
                Go to Browse assets
              </Link>
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Skeleton variant="text" width={200} height={40} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 1, mb: 2 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1, mb: 1 }} />
            <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
          </Box>
        </Paper>
      </Box>
    );
  }

  if (error && !details) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
            <Button variant="contained" onClick={loadDetails}>
              Retry
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (details && !isVariantLayer(details.layer)) {
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
        <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
            <Alert severity="warning">
              This layer does not support variants.{' '}
              <Link component={RouterLink} to="/search-assets">
                Go to Browse assets
              </Link>
            </Alert>
          </Box>
        </Paper>
      </Box>
    );
  }

  if (!baseAsset) return null;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Paper elevation={2} sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto', p: 3 }}>
          <Typography variant="h5" gutterBottom>
            Create variant
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Register a variant for the base asset below. Variant type and name are required; upload a file that matches the layer.
          </Typography>

          {/* Base asset: name from metadata, layer, category/subcategory, thumbnail, description, HFN, MFA */}
          <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1 }}>
              Base asset (read-only)
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                  <Box
                    sx={{
                      height: 280,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'grey.100',
                    }}
                  >
                    <AssetThumbnail asset={baseAsset} width={280} height={280} />
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                  {displayName}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
                  <Chip
                    label={LAYER_LABELS[layer] ?? layer}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                  {taxonomyPath && (
                    <Chip label={taxonomyPath} variant="outlined" size="small" />
                  )}
                </Stack>
                {details?.description && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Description
                    </Typography>
                    <Typography variant="body1">{details.description}</Typography>
                  </Box>
                )}
                {tags?.tags?.length ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Tags
                    </Typography>
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {tags.tags.map((tag, i) => (
                        <Chip key={i} label={tag} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  </Box>
                ) : null}
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    HFN
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {details?.humanFriendlyName ?? '—'}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    MFA
                  </Typography>
                  <Typography variant="body2" fontFamily="monospace" sx={{ wordBreak: 'break-all' }}>
                    {mfa || '—'}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            {metadataError && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                {metadataError}
              </Alert>
            )}
            {layerMetadata && details && METADATA_LABEL_KEYS[details.layer] && (() => {
              const dataForGrid =
                details.layer === 'G'
                  ? (() => {
                      const { thumbnailUrl: _u, thumbnailPath: _p, ...rest } = layerMetadata as Record<string, unknown>;
                      return rest;
                    })()
                  : (layerMetadata as Record<string, unknown>);
              const keys = METADATA_LABEL_KEYS[details.layer] ?? [];
              return (
                <Accordion defaultExpanded={false} sx={{ mt: 2, boxShadow: 'none', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {LAYER_LABELS[details.layer] ?? details.layer} metadata
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <ReadOnlyMetadataGrid data={dataForGrid} allKeys={keys} />
                  </AccordionDetails>
                </Accordion>
              );
            })()}
          </Box>

          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Variant details
            </Typography>

            <Grid container spacing={2} sx={{ mt: 1, mb: 2 }}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth required>
                  <InputLabel id="variant-type-label">Variant type</InputLabel>
                  <Select
                    labelId="variant-type-label"
                    value={variantType}
                    label="Variant type"
                    onChange={(e: SelectChangeEvent<string>) => setVariantType(e.target.value)}
                  >
                    {variantTypeOptions.map(opt => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  required
                  label="Variant name"
                  value={variantName}
                  onChange={e => setVariantName(e.target.value.slice(0, 255))}
                  helperText={`Required, max 255 characters (${variantName.length}/255)`}
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 2 }}>
              <Button variant="outlined" component="label">
                Choose file
                <input
                  type="file"
                  hidden
                  accept={accept}
                  onChange={handleFileChange}
                />
              </Button>
              {file && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {file.name} ({(file.size / 1024).toFixed(1)} KB)
                </Typography>
              )}
              {fileError && (
                <Alert severity="error" sx={{ mt: 1 }}>
                  {fileError}
                </Alert>
              )}
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                Max 100 MB. Allowed for {LAYER_LABELS[layer]}: {uploadConfig?.extensions.join(', ')}
              </Typography>
            </Box>

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError('')}>
                {submitError}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button component={RouterLink} to="/search-assets" variant="outlined">
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting}
              >
                {isSubmitting ? <CircularProgress size={24} /> : 'Register variant'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default CreateVariantPage;
